import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Sparkles, ChevronDown, ArrowRight, Check, Play,
  Wand2, Camera, Gift, TrendingUp, Shield, Zap, MessageCircle,
  Globe, Share2, Menu, X, Mail, Phone, Send,
  BookOpen, BedDouble, MapPin, Box, LogIn
} from 'lucide-react';

// Lucide não inclui ícones de marca (Instagram é logo, não glifo genérico) —
// desenhado à mão no mesmo estilo stroke-based dos demais ícones do lucide.
function InstagramIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

// Mesmo número usado no botão flutuante de suporte (WhatsAppFab.jsx) — uma
// única fonte de verdade via .env pra não desalinhar entre as duas telas.
const SUPPORT_WHATSAPP = import.meta.env.VITE_SUPPORT_WHATSAPP || '5511977642485';
const SUPPORT_EMAIL = 'suporte@mimodocasal.com.br';
const INSTAGRAM_URL = 'https://www.instagram.com/mimodocasal';
import { FAQ_ITEMS, WEDDING_THEMES } from '../data/mockData';
import { MIMO3D_MODELS } from '../data/mimo3dModels';
import { formatCurrency } from '../lib/utils';
import { TemplatePreviewCard } from '../components/theme/TemplatePreviewCard';
import { Button } from '../components/ui/Button';
import { getActiveReferral } from '../lib/referral';

const EXAMPLE_WEDDING_SLUG = 'v7x8hy-rodrigo-e-bruna';

/* ─── Presentes reais, gerados por casais reais (uso autorizado) ─── */
// Lista curada pelo Matheus (jul/2026) — casais reais (Bruna & Rodrigo,
// Isa & Leo) autorizaram o uso das artes, incluindo o rosto dos noivos.
const REAL_GIFTS = [
  { id: 'real-alexa', title: 'Assistente de Voz pra Casa Nova', description: '"Alexa, toca a nossa música do casamento." Presente que vira rotina.', suggestedValue: 140, image: '/images/real-gifts/alexa.png', emoji: '🔊' },
  { id: 'real-arranhador', title: 'Playground pro Gato', description: 'Arranhador novo pro bichano brincar sem destruir o sofá da sala.', suggestedValue: 220, image: '/images/real-gifts/playground-blade.png', emoji: '🐈' },
  { id: 'real-transportadora', title: 'Transportadora de Viagem', description: 'Pra galera de quatro patas também curtir a mudança pra casa nova.', suggestedValue: 130, image: '/images/real-gifts/passagem-gatos.png', emoji: '✈️' },
  { id: 'real-sache-gatas', title: 'Um Ano de Sachê pras Gatas', description: 'Estoque de sachê pra dupla de gatas não passar fome nunca.', suggestedValue: 180, image: '/images/real-gifts/sache-gatas.png', emoji: '🐱' },
  { id: 'real-ong-gatos', title: 'Ajuda pra Montar uma ONG de Gatos', description: 'Uma mãozinha pro sonho de resgatar e cuidar de gatinhos sair do papel.', suggestedValue: 300, image: '/images/real-gifts/ong-gatos.png', emoji: '🐾' },
  { id: 'real-cama-king', title: 'Cama King Size para o Casal', description: 'Espaço de sobra pro casal e pra dupla de gatos que também mora ali.', suggestedValue: 350, image: '/images/real-gifts/cama-king-size.png', emoji: '🛏️' },
  { id: 'real-almoco-italia', title: 'Almoço Romântico na Itália', description: 'Vinho, queijo e vinhedo ao fundo — um brinde de lua de mel que vira lembrança pra sempre.', suggestedValue: 260, image: '/images/real-gifts/almoco-italia.png', emoji: '🍷' },
  { id: 'real-mensalidade-shape', title: 'Mensalidade para Entrar no Shape', description: 'Um mês de academia pros dois — treino é sempre melhor em dupla.', suggestedValue: 150, image: '/images/real-gifts/mensalidade-shape.png', emoji: '💪' },
  { id: 'real-upgrade-voo', title: 'Upgrade de Conforto no Voo', description: 'Do check-in ao desembarque, a lua de mel já começa relaxante lá em cima.', suggestedValue: 320, image: '/images/real-gifts/upgrade-voo.png', emoji: '🥂' },
];

/* ─── Helpers ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } },
};

// Brilho que segue o cursor — só pras sessões escuras (mesmo efeito usado em
// sites como Linear/Vercel). Sem lib nova, só estado local: o <section> que
// usa isso passa onMouseMove={makeSpotlightHandler(setSpot)} pra si mesmo e
// renderiza <SpotlightOverlay spot={spot} /> como primeiro filho.
function makeSpotlightHandler(setSpot) {
  return (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };
}

function SpotlightOverlay({ spot }) {
  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ background: `radial-gradient(600px circle at ${spot.x}% ${spot.y}%, rgba(255,255,255,0.07), transparent 60%)` }}
    />
  );
}

/* ─── Navbar ─── */
function Navbar({ topOffset = 0 }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Presentes', href: '#presentes' },
    { label: 'Como Funciona', href: '#como-funciona' },
    { label: 'Casais reais', href: '#casamentos-reais' },
    { label: 'Templates', href: '#templates' },
    { label: 'Preços', href: '#precos' },
    { label: 'Mimos 3D', href: '#mimos-3d' },
    { label: 'Blog', href: '/blog' },
    { label: 'Alguma dúvida?', href: '#contato' },
  ];

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-rose-100' : 'bg-transparent'
      }`}
      style={{ top: topOffset }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl shadow-md group-hover:shadow-lg transition-shadow" />
            <span className={`font-bold text-xl transition-colors ${scrolled ? 'text-gray-900' : 'text-white'}`}>
              Mimo do Casal
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map(({ label, href }) => (
              <a
                key={label}
                href={href}
                className={`text-sm font-medium transition-colors hover:text-rose-400 ${scrolled ? 'text-gray-600' : 'text-white/85'}`}
              >
                {label}
              </a>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/login"
              className={`text-sm font-semibold transition-colors px-4 py-2 rounded-xl ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
            >
              Entrar
            </Link>
            <Link to="/signup">
              <Button size="md" className="shadow-romantic">
                Começar Grátis
                <ArrowRight size={16} />
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-gray-100 shadow-lg overflow-hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map(({ label, href }) => (
                <a
                  key={label}
                  href={href}
                  onClick={() => setOpen(false)}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                >
                  {label}
                </a>
              ))}
              <div className="border-t border-gray-100 mt-2 pt-3 flex flex-col gap-2">
                <Link to="/login" className="px-4 py-3 text-sm font-semibold text-gray-700 text-center rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors">
                  Entrar
                </Link>
                <Link to="/signup">
                  <Button size="md" className="w-full">
                    Começar Grátis <ArrowRight size={16} />
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

/* ─── Hero ─── */
function Hero() {
  const photoStack = [
    {
      src: '/images/real-gifts/alexa.png',
      caption: 'Assistente de Voz',
      initRotate: -10,
      overlay: 'rgba(244,63,94,0.10)',
      top: 15, left: -5,
      w: 198,
      dur: 8,
    },
    {
      src: '/images/real-gifts/mensalidade-shape.png',
      caption: 'Mês de Academia',
      initRotate: 7,
      overlay: 'rgba(245,158,11,0.08)',
      top: 0, left: 230,
      w: 204,
      dur: 7,
    },
    {
      src: '/images/real-gifts/ong-gatos.png',
      caption: 'ONG de Gatos',
      initRotate: 5,
      overlay: 'rgba(236,72,153,0.09)',
      top: 278, left: 8,
      w: 192,
      dur: 9,
    },
    {
      src: '/images/real-gifts/upgrade-voo.png',
      caption: 'Upgrade de Voo',
      initRotate: -8,
      overlay: null,
      top: 262, left: 218,
      w: 208,
      dur: 6,
    },
  ];

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background wedding photo */}
      <img
        src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=80"
        alt="Casamento"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Overlay: darker on the left (text), lighter on the right (photos) */}
      <div className="absolute inset-0 bg-gradient-to-r from-rose-950/92 via-rose-950/78 to-rose-950/45" />

      {/* Floating flower petals */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        {[
          { emoji: '🌸', x: 7,  dur: 11, delay: 0,   size: 22 },
          { emoji: '🌺', x: 19, dur: 14, delay: 2.2, size: 17 },
          { emoji: '🌸', x: 33, dur: 10, delay: 4.8, size: 20 },
          { emoji: '🌼', x: 50, dur: 13, delay: 1.0, size: 16 },
          { emoji: '🌸', x: 63, dur: 12, delay: 3.5, size: 21 },
          { emoji: '🌺', x: 78, dur: 11, delay: 5.5, size: 18 },
          { emoji: '🌸', x: 90, dur: 15, delay: 0.7, size: 15 },
        ].map((p, i) => (
          <motion.div
            key={i}
            className="absolute select-none"
            style={{ left: `${p.x}%`, top: -50, fontSize: p.size, opacity: 0 }}
            animate={{ y: [0, 960], rotate: [0, 360 * (i % 2 ? 1 : -1)], opacity: [0, 0.72, 0.72, 0] }}
            transition={{ duration: p.dur, repeat: Infinity, ease: 'linear', delay: p.delay }}
          >
            {p.emoji}
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-16 lg:pt-32 lg:pb-20">
        <div className="grid lg:grid-cols-[1fr_460px] xl:grid-cols-[1fr_520px] gap-12 xl:gap-20 items-center">

          {/* ── Left: editorial typography ── */}
          <div className="relative z-10">
            {/* Script accent */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-8"
            >
              <div className="w-10 h-px bg-rose-400/70" />
              <span className="text-rose-300 text-lg" style={{ fontFamily: "'Dancing Script', cursive" }}>
                para o dia mais especial
              </span>
            </motion.div>

            {/* Giant stacked headline */}
            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="leading-[0.87] mb-8"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              <span className="block text-[clamp(52px,8vw,96px)] font-bold text-white">
                A lista
              </span>
              <span className="block text-[clamp(52px,8vw,96px)] font-bold italic text-rose-300">
                de presentes
              </span>
              <span className="block text-[clamp(36px,5.5vw,68px)] font-normal text-white/55 mt-1">
                dos seus sonhos.
              </span>
            </motion.h1>

            {/* Thin decorative rule */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="w-20 h-px bg-rose-400/50 mb-8 origin-left"
            />

            {/* Subtext */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-white/65 text-lg max-w-md mb-10 leading-relaxed"
            >
              Crie uma página personalizada, adicione presentes simbólicos e compartilhe com quem vocês amam. Sua{' '}
              <span className="text-rose-300 font-medium">IA exclusiva</span> transforma fotos do casal em cards de presente ilustrados — uma história em cada presente.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.68 }}
              className="flex flex-col sm:flex-row gap-4 items-start"
            >
              <Link to="/signup">
                <Button size="xl" className="group shadow-lg shadow-rose-900/50">
                  <Heart size={20} fill="white" />
                  Criar minha página grátis
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <a
                href="#como-funciona"
                className="inline-flex items-center gap-2 px-6 py-4 text-white/60 font-semibold hover:text-white transition-colors rounded-xl hover:bg-white/10"
              >
                <Play size={15} className="fill-white/50" />
                Ver como funciona
              </a>
            </motion.div>

          </div>

          {/* ── Right: 4-photo scattered grid ── */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="hidden lg:block relative h-[560px]"
          >
            {photoStack.map((photo, i) => (
              <motion.div
                key={i}
                className="absolute bg-white p-3 pb-10"
                style={{
                  top: `${photo.top}px`,
                  left: `${photo.left}px`,
                  width: `${photo.w}px`,
                  zIndex: i + 1,
                  boxShadow: '0 20px 56px rgba(0,0,0,0.40)',
                }}
                initial={{ rotate: photo.initRotate }}
                animate={{ rotate: [photo.initRotate - 1.2, photo.initRotate + 1.2, photo.initRotate - 1.2] }}
                transition={{ duration: photo.dur, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 }}
              >
                <div className="relative w-full aspect-square overflow-hidden">
                  <img
                    src={photo.src}
                    alt={photo.caption}
                    className="w-full h-full object-cover"
                  />
                  {photo.overlay && (
                    <div className="absolute inset-0" style={{ background: photo.overlay }} />
                  )}
                </div>
                <p
                  className="text-center mt-2 text-gray-400"
                  style={{ fontFamily: "'Dancing Script', cursive", fontSize: '14px' }}
                >
                  {photo.caption}
                </p>
              </motion.div>
            ))}
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─── Stats bar ─── */
function StatsBar() {
  const stats = [
    { value: '2,99%', label: 'Comissão por presente, sem mensalidade', icon: <Zap size={20} className="text-amber-300" /> },
    { value: 'R$ 0', label: 'Para criar a página', icon: <Heart size={20} className="text-rose-200" fill="currentColor" /> },
    { value: 'R$ 5', label: 'Taxa de saque', icon: <Shield size={20} className="text-emerald-300" /> },
    { value: 'Pix', label: 'Ou cartão em até 6x', icon: <Gift size={20} className="text-amber-200" /> },
  ];

  return (
    <section className="bg-rose-700 py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0 lg:divide-x lg:divide-white/20">
          {stats.map(({ value, label, icon }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex flex-col items-center gap-2 px-6 py-2"
            >
              {icon}
              <p className="text-3xl font-bold text-white">{value}</p>
              <p className="text-white/70 text-sm text-center">{label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How it works ─── */
function HowItWorks() {
  const steps = [
    {
      n: '01',
      icon: <Camera size={28} className="text-rose-500" />,
      title: 'Crie sua conta grátis',
      desc: 'O site é 100% gratuito. Cadastre-se, envie fotos de vocês dois e conte a história do casal — sem mensalidade e sem cartão de crédito.',
    },
    {
      n: '02',
      icon: <Wand2 size={28} className="text-amber-500" />,
      title: 'Presentes com a cara de vocês',
      desc: 'Nossa IA cria ilustrações personalizadas com o rosto dos noivos em cada presente: cartoon 3D, aquarela, anime, editorial e muito mais.',
    },
    {
      n: '03',
      icon: <Globe size={28} className="text-rose-500" />,
      title: 'Personalize e compartilhe',
      desc: 'Escolha o tema, monte a página em minutos e envie o link aos convidados. Inclua local da cerimônia e festa, hospedagens recomendadas e o manual do convidado.',
    },
    {
      n: '04',
      icon: <Gift size={28} className="text-rose-500" />,
      title: 'Receba via Pix ou cartão',
      desc: 'O convidado escolhe o presente e paga por Pix ou no cartão, parcelado em até 6x (você decide o teto). Criar a página não tem mensalidade — a comissão é de 2,99% por presente recebido.',
    },
  ];

  return (
    <section id="como-funciona" className="py-14 sm:py-24 bg-gradient-to-b from-white to-rose-50/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-full px-4 py-2 mb-4">
            <Sparkles size={14} className="text-rose-400" />
            <span className="text-rose-600 text-sm font-medium">Simples assim</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Como funciona?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
            Criar é grátis. A comissão é de 2,99% por presente, sem mensalidade,
            e a página vira um convite completo para os convidados.
          </motion.p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map(({ n, icon, title, desc }, i) => (
            <motion.div
              key={n}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ delay: i * 0.15 }}
              className="relative z-10 flex flex-col items-center text-center"
            >
              {/* Number circle */}
              <div className="relative mb-6">
                <div className="w-16 h-16 bg-white border-2 border-rose-200 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-100">
                  {icon}
                </div>
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-rose-600 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-xs font-bold">{n.slice(-1)}</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
              <p className="text-gray-500 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Price Highlight (logo após "Como funciona") ─── */
function PriceHighlight() {
  return (
    <section className="relative py-14 sm:py-20 bg-rose-100 overflow-hidden">
      {/* Ornamento sutil de fundo */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute top-10 -left-20 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 -right-20 w-72 h-72 bg-pink-200/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Chips */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="flex flex-wrap gap-2.5 justify-center mb-6"
        >
          <motion.span variants={fadeUp} className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3.5 py-1.5 text-emerald-700 text-xs sm:text-sm font-semibold">
            <Check size={12} className="text-emerald-600" />
            Plataforma 100% gratuita
          </motion.span>
          <motion.span variants={fadeUp} className="inline-flex items-center gap-1.5 bg-rose-50 border border-rose-200 rounded-full px-3.5 py-1.5 text-rose-600 text-xs sm:text-sm font-semibold">
            Só 2,99% de comissão
          </motion.span>
          <motion.span variants={fadeUp} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3.5 py-1.5 text-amber-700 text-xs sm:text-sm font-semibold">
            <Zap size={12} className="text-amber-600" />
            Menor comissão do mercado
          </motion.span>
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Crie grátis. <span style={{ color: '#7c1235' }}>Pague só pelo que arrecadar.</span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          Página, presentes, IA e analytics — tudo gratuito. Ficamos com apenas{' '}
          <strong className="text-gray-800">2,99% do que você arrecadar</strong>,
          a menor comissão do mercado. Sem mensalidade. Sem pegadinhas.
        </motion.p>

        {/* Big commission display + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 inline-flex flex-col sm:flex-row items-center gap-6 sm:gap-10 bg-white border border-rose-100 rounded-3xl shadow-romantic px-8 py-7"
        >
          <div className="flex items-end gap-2">
            <span className="text-6xl sm:text-7xl font-bold text-rose-500 leading-none" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>2,99</span>
            <div className="pb-1.5 text-left">
              <span className="text-2xl sm:text-3xl font-bold text-rose-400">%</span>
              <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">sobre o arrecadado</p>
            </div>
          </div>
          <div className="hidden sm:block w-px h-16 bg-rose-100" />
          <div className="flex flex-col items-center sm:items-start gap-2">
            <Link to="/signup">
              <Button size="lg" className="w-full sm:w-auto">
                Criar minha página grátis
                <ArrowRight size={16} />
              </Button>
            </Link>
            <p className="text-xs text-gray-400">Sem cartão. Sem mensalidade. Nunca.</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Gift Cards Showcase ─── */
function GiftShowcase() {
  const showcase = REAL_GIFTS;
  const [spot, setSpot] = useState({ x: 50, y: 50 });

  return (
    <section
      id="presentes"
      onMouseMove={makeSpotlightHandler(setSpot)}
      className="py-14 sm:py-24 bg-gradient-to-br from-rose-950 via-pink-950 to-gray-950 relative overflow-hidden"
    >
      <SpotlightOverlay spot={spot} />
      <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-rose-500/20 rounded-full blur-3xl" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-4">
            <Gift size={14} className="text-amber-300" />
            <span className="text-white/80 text-sm font-medium">Presentes reais, de casais reais</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Presentes que contam histórias
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/70 text-lg max-w-2xl mx-auto">
            Nada de mockup: essas são artes que a nossa IA gerou de verdade para casais que já usam o Mimo do Casal. Cada presente vira uma experiência afetiva com a cara do casal.
          </motion.p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {showcase.map((card, i) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl transition-shadow overflow-hidden group cursor-pointer"
            >
              <div className="aspect-[4/3] overflow-hidden relative">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-white/90 backdrop-blur rounded-full px-3 py-1 text-xs font-semibold text-gray-700 flex items-center gap-1">
                    <span>{card.emoji}</span>
                    Presente real
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-bold text-gray-900 mb-1">{card.title}</h3>
                <p className="text-gray-500 text-sm mb-4 line-clamp-2">{card.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-bold text-rose-600">{formatCurrency(card.suggestedValue)}</span>
                  <span className="bg-rose-50 text-rose-600 text-xs font-semibold px-3 py-1 rounded-full">
                    Adicionar
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <Link to="/signup">
            <Button variant="secondary" size="lg">
              Ver todos os cards
              <ArrowRight size={16} />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── AI Section ─── */
function AISection() {
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const features = [
    { icon: <Camera size={20} />, text: 'Envie suas fotos do casal' },
    { icon: <Wand2 size={20} />, text: 'IA gera ilustrações em segundos' },
    { icon: <Sparkles size={20} />, text: '7 estilos artísticos disponíveis' },
    { icon: <Gift size={20} />, text: 'Cada presente com arte única' },
  ];

  return (
    <section
      id="ia-generativa"
      onMouseMove={makeSpotlightHandler(setSpot)}
      className="py-14 sm:py-24 bg-gradient-to-br from-rose-950 via-pink-950 to-gray-950 relative overflow-hidden"
    >
      <SpotlightOverlay spot={spot} />
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-rose-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: AI cards preview */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 gap-4"
          >
            {[
              { style: 'Cartoon 3D', img: '/images/examples/casamento-cartoon3D.png', color: 'from-violet-500/80' },
              { style: 'Aquarela', img: '/images/examples/casamento-aquarela.jpg', color: 'from-rose-500/80' },
              { style: 'Anime', img: '/images/examples/casamento-anime.png', color: 'from-pink-500/80' },
              { style: 'Editorial', img: '/images/examples/casamento-editorial.png', color: 'from-amber-500/80' },
            ].map(({ style, img, color }, i) => (
              <motion.div
                key={style}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ scale: 1.04 }}
                className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group"
              >
                <img
                  src={img}
                  alt={`Exemplo de ilustração estilo ${style}`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${color} to-transparent opacity-70`} />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-white text-xs font-bold bg-white/20 backdrop-blur rounded-lg px-2 py-1">{style}</span>
                </div>
                <div className="absolute top-2 right-2">
                  <Wand2 size={16} className="text-white/80" />
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Right: Text */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6">
              <Wand2 size={14} className="text-rose-300" />
              <span className="text-white/80 text-sm font-medium">IA generativa exclusiva</span>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Ilustrações únicas{' '}
              <em className="not-italic text-amber-300 font-bold">
                do seu casal
              </em>{' '}
              em cada presente
            </motion.h2>

            <motion.p variants={fadeUp} className="text-white/70 text-lg mb-8 leading-relaxed">
              Nossa IA transforma fotos reais do casal em obras de arte personalizadas. Cada card de presente vira uma ilustração única com vocês dois como protagonistas.
            </motion.p>

            <motion.div variants={stagger} className="space-y-4 mb-10">
              {features.map(({ icon, text }) => (
                <motion.div key={text} variants={fadeUp} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-rose-500/20 border border-rose-500/30 rounded-lg flex items-center justify-center text-rose-300 flex-shrink-0">
                    {icon}
                  </div>
                  <span className="text-white/80">{text}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div variants={fadeUp}>
              <Link to="/signup">
                <Button size="lg" className="shadow-2xl shadow-rose-900/50">
                  <Sparkles size={18} />
                  Experimentar IA Grátis
                  <ArrowRight size={16} />
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


/* ─── Mimos em 3D (parceria com a T&C 3D — lembrancinhas impressas) ─── */
function Mimos3DSection() {
  const preview = MIMO3D_MODELS.slice(0, 6);

  return (
    <section id="mimos-3d" className="py-14 sm:py-24 bg-gradient-to-b from-white to-rose-50/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-full px-4 py-2 mb-4">
            <Box size={14} className="text-rose-500" />
            <span className="text-rose-700 text-sm font-medium">Novidade · parceria exclusiva</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Mimos em 3D pro seu casamento
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
            Além da lista de presentes online, agora você também pode encomendar lembrancinhas e
            detalhes impressos em 3D — do topo de bolo ao marcador de lugar.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 max-w-4xl mx-auto">
          {preview.map((model, i) => (
            <motion.div
              key={model.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-shadow overflow-hidden"
            >
              <div className="aspect-square overflow-hidden">
                <img src={model.image} alt={model.label} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <h3 className="font-semibold text-gray-900 text-[11px] leading-tight line-clamp-2">{model.label}</h3>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10"
        >
          <Link to="/signup">
            <Button size="lg">
              Quero oferecer isso no meu casamento
              <ArrowRight size={16} />
            </Button>
          </Link>
          <Link to="/dashboard/mimos3d">
            <Button variant="outline" size="lg">
              <LogIn size={16} />
              Já sou cliente, ver no painel
            </Button>
          </Link>
        </motion.div>
        <p className="text-center text-xs text-gray-400 mt-4">
          Imagens conceituais do catálogo — as fotos reais chegam conforme as peças são produzidas.
        </p>
      </div>
    </section>
  );
}

/* ─── Templates (reflete a arte ilustrada real do produto) ─── */
function TemplatesSection() {
  const [active, setActive] = useState('orquidea-tropical');
  return (
    <section id="templates" className="py-14 sm:py-24 bg-gradient-to-b from-rose-50/40 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-full px-4 py-2 mb-4">
            <Sparkles size={14} className="text-rose-400" />
            <span className="text-rose-600 text-sm font-medium">Convites ilustrados, um por arquetipo</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Vários templates disponíveis.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
            Cada tema gera um convite ilustrado próprio &mdash; arco floral, monograma, tipografia e
            paleta exclusivos. Veja exatamente como a página dos noivos vai aparecer.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {['orquidea-tropical', 'gatsby-deco', 'cerejeira-japonesa', 'mediterranea', 'eucalipto-sage', 'noite-estrelada', 'aquarela-floral']
            .map((id) => WEDDING_THEMES.find((t) => t.id === id))
            .filter(Boolean)
            .map((theme, index) => (
              <TemplatePreviewCard
                key={theme.id}
                theme={theme}
                index={index}
                selected={active === theme.id}
                onSelect={(themeId) => setActive(themeId)}
              />
            ))}
          {/* Placeholder "e muito mais" */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.28 }}
            className="rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/40 flex flex-col items-center justify-center text-center p-8 min-h-[280px]"
          >
            <Sparkles size={28} className="text-rose-400 mb-3" />
            <p className="text-2xl font-bold text-rose-500" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              e muito mais ...
            </p>
            <p className="text-sm text-gray-500 mt-2 max-w-[200px]">
              Dezenas de estilos para combinar com a personalidade de cada casal.
            </p>
          </motion.div>
        </div>

        <div className="text-center mt-12">
          <a
            href={`/${EXAMPLE_WEDDING_SLUG}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-rose-500 text-white font-semibold hover:bg-rose-600 transition-colors shadow-lg shadow-rose-200"
          >
            Ver uma pagina de verdade <ArrowRight size={17} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── Casamentos reais feitos no Mimo do Casal ─── */
// Thumbs gerados a partir de screenshot real das páginas ao vivo (mobile,
// hero acima da dobra) — tools/_tmp-capture-real-weddings.mjs, ago/2026.
// Atualizar a imagem manualmente se o tema/conteúdo dessas páginas mudar.
const REAL_WEDDINGS = [
  { slug: 'v7x8hy-rodrigo-e-bruna', name1: 'Bruna', name2: 'Rodrigo', thumb: '/images/real-weddings/rodrigo-e-bruna.jpg' },
  { slug: 'tmvvvq-isaeleo', name1: 'Isa', name2: 'Leo', thumb: '/images/real-weddings/isa-e-leo.jpg' },
];

function RealWeddingsSection() {
  return (
    <section id="casamentos-reais" className="py-14 sm:py-24 bg-gradient-to-b from-rose-50/40 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-14"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2 mb-4">
            <Heart size={14} className="text-emerald-500" fill="currentColor" />
            <span className="text-emerald-700 text-sm font-medium">Casamentos reais</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Casais que já celebraram com o Mimo do Casal
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-500 text-lg max-w-2xl mx-auto">
            Nada de mockup. Clique e navegue numa página real, no ar, recebendo presentes e confirmações de verdade.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-2 gap-5 sm:gap-10 max-w-xl mx-auto">
          {REAL_WEDDINGS.map(({ slug, name1, name2, thumb }, i) => (
            <motion.a
              key={slug}
              href={`/${slug}`}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className="group block"
            >
              <div className="relative aspect-[9/16] rounded-[1.75rem] border-[5px] border-gray-900 bg-gray-900 overflow-hidden shadow-xl shadow-rose-200/50 group-hover:shadow-2xl group-hover:shadow-rose-300/70 transition-shadow duration-300">
                <img
                  src={thumb}
                  alt={`Página real de casamento de ${name1} & ${name2}, feita no Mimo do Casal`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-3 sm:bottom-4 flex justify-center px-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-white/95 text-gray-900 text-[11px] sm:text-xs font-semibold shadow-lg group-hover:bg-rose-500 group-hover:text-white transition-colors">
                    Ver página real <ArrowRight size={12} />
                  </span>
                </div>
              </div>
              <p className="text-center mt-4 text-lg sm:text-xl text-gray-900" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {name1}
                <span className="mx-1.5 align-middle" style={{ fontFamily: "'Dancing Script', cursive", color: '#7c1235', fontSize: '0.75em' }}>&amp;</span>
                {name2}
              </p>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Pricing ─── */
function PricingSection() {
  const perks = [
    { icon: <Gift size={16} />, text: 'Presentes ilimitados na sua lista' },
    { icon: <Sparkles size={16} />, text: 'IA para ilustrações personalizadas do casal' },
    { icon: <Globe size={16} />, text: 'Página com tema visual e URL própria' },
    { icon: <MapPin size={16} />, text: 'Local da cerimônia e festa com mapa' },
    { icon: <BedDouble size={16} />, text: 'Sugestões de hospedagem para os convidados' },
    { icon: <BookOpen size={16} />, text: 'Manual do convidado completo' },
    { icon: <Zap size={16} />, text: 'Pix direto na sua conta, em tempo real' },
    { icon: <TrendingUp size={16} />, text: 'Dashboard com analytics completo' },
    { icon: <MessageCircle size={16} />, text: 'Mensagens e recados dos convidados' },
    { icon: <Share2 size={16} />, text: 'Link para compartilhamento' },
    { icon: <Shield size={16} />, text: 'Suporte via WhatsApp' },
  ];

  const comparison = [
    { feature: 'Mensalidade', mimo: 'Grátis', others: 'R$ 50–300/mês' },
    { feature: 'Comissão', mimo: '2,99%', others: '3–4%' },
    { feature: 'Taxa de saque', mimo: 'R$ 5', others: 'R$ 5–15' },
    { feature: 'Pix em tempo real', mimo: '✓', others: 'Às vezes' },
    { feature: 'IA personalizada', mimo: '✓', others: '✗' },
    { feature: 'Página com tema', mimo: '✓', others: 'Básico' },
  ];

  const [spot, setSpot] = useState({ x: 50, y: 50 });

  return (
    <section
      id="precos"
      onMouseMove={makeSpotlightHandler(setSpot)}
      className="py-14 sm:py-24 bg-gradient-to-br from-rose-950 via-pink-950 to-gray-950 relative overflow-hidden"
    >
      <SpotlightOverlay spot={spot} />
      <div className="absolute top-0 right-0 w-96 h-96 bg-pink-500/15 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-rose-500/15 rounded-full blur-3xl" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-12"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-4">
            <Shield size={14} className="text-rose-300" />
            <span className="text-white/80 text-sm font-medium">Compare e decida</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Por que escolher o <span className="text-amber-300">Mimo do Casal</span>?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto">
            Lado a lado com os principais concorrentes do mercado — sem letras miúdas.
          </motion.p>
        </motion.div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white border border-gray-100 rounded-3xl p-5 sm:p-8 shadow-sm"
        >
          {/* Header row */}
          <div className="grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1.4fr_120px_120px] gap-2 pb-4 border-b-2 border-gray-100">
            <span className="text-xs text-gray-400 uppercase tracking-wide">Recurso</span>
            <span className="text-xs sm:text-sm font-bold text-rose-500 text-center">
              <span className="inline-flex items-center gap-1">
                <Heart size={12} fill="#7c1235" className="text-rose-500" />
                Mimo
              </span>
            </span>
            <span className="text-xs sm:text-sm text-gray-400 text-center">Concorrentes</span>
          </div>

          <div className="divide-y divide-gray-100">
            {comparison.map(({ feature, mimo, others }) => (
              <div key={feature} className="grid grid-cols-[1fr_100px_100px] sm:grid-cols-[1.4fr_120px_120px] gap-2 py-4 items-center">
                <span className="text-sm sm:text-base text-gray-700 font-medium">{feature}</span>
                <span className="text-sm sm:text-base font-semibold text-emerald-600 text-center">{mimo}</span>
                <span className="text-sm sm:text-base text-gray-400 text-center">{others}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Perks */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          {perks.map(({ icon, text }) => (
            <div key={text} className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-4 py-3">
              <div className="w-9 h-9 bg-rose-50 rounded-lg flex items-center justify-center text-rose-500 flex-shrink-0">
                {icon}
              </div>
              <span className="text-sm font-medium text-gray-700">{text}</span>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 text-center"
        >
          <Link to="/signup">
            <Button size="lg">
              Quero a página dos meus sonhos
              <ArrowRight size={16} />
            </Button>
          </Link>
          <p className="text-xs text-white/50 mt-3">Comece em menos de 5 minutos. Sem compromisso.</p>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Contact ─── */
function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.name || form.name.trim().length < 2) { setError('Informe seu nome.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('Informe um e-mail válido.'); return; }
    if (!form.subject) { setError('Selecione um assunto.'); return; }
    if (!form.message || form.message.trim().length < 20) { setError('Conte com um pouco mais de detalhe na mensagem.'); return; }
    setSending(true);
    try {
      const response = await fetch('/api/submitContact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body?.error?.message || 'Não foi possível enviar agora.');
      }
      setSent(true);
    } catch (err) {
      setError(err?.message || 'Erro ao enviar. Tente novamente em alguns instantes.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contato" className="py-14 sm:py-24 bg-rose-600 relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-300/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* Left: info */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-white/15 border border-white/20 rounded-full px-4 py-1.5 text-white text-sm font-medium mb-6">
              <MessageCircle size={14} />
              Fale com a gente
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="text-4xl font-bold text-white mb-4 leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Alguma dúvida? Estamos aqui.
            </motion.h2>

            <motion.p variants={fadeUp} className="text-white/75 text-lg leading-relaxed mb-8">
              Nossa equipe responde em até 24 horas. Pode perguntar sobre a plataforma, planos, ou só dizer oi.
            </motion.p>

            <motion.div variants={stagger} className="space-y-4">
              {[
                { icon: <Mail size={18} />, label: 'Email', value: SUPPORT_EMAIL },
                { icon: <Phone size={18} />, label: 'WhatsApp', value: 'Suporte via WhatsApp' },
                { icon: <MessageCircle size={18} />, label: 'Resposta', value: 'Em até 24 horas' },
              ].map(({ icon, label, value }) => (
                <motion.div key={label} variants={fadeUp} className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-white/15 border border-white/20 rounded-xl flex items-center justify-center text-white flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <p className="text-white/60 text-xs font-medium">{label}</p>
                    <p className="text-white font-semibold text-sm">{value}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white rounded-3xl p-8 shadow-2xl shadow-rose-900/30">
              <AnimatePresence mode="wait">
                {!sent ? (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <h3 className="text-xl font-bold text-gray-900 mb-5">Envie uma mensagem</h3>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nome</label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={set('name')}
                          placeholder="Seu nome"
                          className="w-full px-3.5 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email</label>
                        <input
                          type="email"
                          value={form.email}
                          onChange={set('email')}
                          placeholder="seu@email.com"
                          className="w-full px-3.5 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Assunto</label>
                      <select
                        value={form.subject}
                        onChange={set('subject')}
                        className="w-full px-3.5 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 bg-white cursor-pointer focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all"
                      >
                        <option value="">Selecione um assunto</option>
                        <option value="duvida">Dúvida geral</option>
                        <option value="tecnico">Problema técnico</option>
                        <option value="parceria">Parceria</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">Mensagem</label>
                      <textarea
                        value={form.message}
                        onChange={set('message')}
                        placeholder="Como podemos ajudar?"
                        rows={4}
                        className="w-full px-3.5 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 resize-none focus:outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100 transition-all"
                      />
                    </div>

                    {error && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={sending}
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-rose-600 text-white font-semibold rounded-xl hover:bg-rose-700 transition-all disabled:opacity-70 shadow-lg shadow-rose-200"
                    >
                      {sending ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          Enviar mensagem
                        </>
                      )}
                    </button>
                  </motion.form>
                ) : (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Check size={28} className="text-emerald-500" strokeWidth={3} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Mensagem enviada!</h3>
                    <p className="text-gray-500 text-sm">Respondemos em até 24 horas. Obrigado pelo contato.</p>
                    <button
                      onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                      className="mt-6 text-sm text-rose-500 hover:text-rose-600 font-semibold transition-colors"
                    >
                      Enviar outra mensagem
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── FAQ ─── */
function FAQSection() {
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <section id="faq" className="py-14 sm:py-24 bg-gradient-to-b from-rose-50/40 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-rose-50 border border-rose-100 rounded-full px-4 py-2 mb-4">
            <MessageCircle size={14} className="text-rose-400" />
            <span className="text-rose-600 text-sm font-medium">Tire suas dúvidas</span>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Perguntas frequentes
          </motion.h2>
        </motion.div>

        <div className="flex flex-col gap-3">
          {FAQ_ITEMS.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <button
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between p-6 text-left gap-4"
              >
                <span className="font-semibold text-gray-900">{item.q}</span>
                <motion.div
                  animate={{ rotate: openIdx === i ? 180 : 0 }}
                  transition={{ duration: 0.25 }}
                  className="flex-shrink-0"
                >
                  <ChevronDown size={20} className="text-rose-400" />
                </motion.div>
              </button>
              <AnimatePresence>
                {openIdx === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <div className="px-6 pb-6">
                      <div className="h-px bg-gray-100 mb-4" />
                      <p className="text-gray-600 leading-relaxed">{item.a}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Footer CTA + Footer ─── */
function FooterCTA() {
  return (
    <section className="py-14 sm:py-24 bg-rose-700 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-pink-300/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <Heart size={32} className="text-white" fill="white" />
            </div>
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Pronto para criar a lista de presentes dos seus sonhos?
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/80 text-lg mb-10">
            Junte-se a mais de 50 casais que já usam o Mimo do Casal.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="xl" className="bg-white text-rose-600 hover:bg-rose-50 shadow-2xl shadow-rose-900/30 font-bold">
                <Heart size={20} className="text-rose-500" fill="currentColor" />
                Criar minha página grátis
                <ArrowRight size={18} />
              </Button>
            </Link>
            <Link to="/login">
              <button className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/40 text-white font-semibold rounded-xl hover:bg-white/10 transition-all text-base">
                Já tenho conta
              </button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-gray-950 text-gray-400 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <img src="/brand/icon.png" alt="" className="w-9 h-9 rounded-xl" />
              <span className="font-bold text-xl text-white">Mimo do Casal</span>
            </Link>
            <p className="text-sm leading-relaxed mb-6">
              A plataforma de lista de presentes que transforma o seu casamento em uma experiência mágica.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Mimo do Casal no Instagram"
                title="Instagram"
                className="w-9 h-9 bg-gray-800 hover:bg-rose-900 rounded-lg flex items-center justify-center transition-colors"
              >
                <InstagramIcon size={16} />
              </a>
              <a
                href={`https://wa.me/${SUPPORT_WHATSAPP}`}
                target="_blank"
                rel="noreferrer"
                aria-label="Falar no WhatsApp"
                title="WhatsApp"
                className="w-9 h-9 bg-gray-800 hover:bg-emerald-700 rounded-lg flex items-center justify-center transition-colors"
              >
                <MessageCircle size={16} />
              </a>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                aria-label="Enviar email para o suporte"
                title="Email"
                className="w-9 h-9 bg-gray-800 hover:bg-rose-900 rounded-lg flex items-center justify-center transition-colors"
              >
                <Mail size={16} />
              </a>
            </div>
          </div>

          {/* Produto */}
          <div>
            <h4 className="text-white font-semibold mb-4">Produto</h4>
            <ul className="space-y-2.5">
              <li><a href="#presentes" className="text-sm hover:text-white transition-colors">Presentes de exemplo</a></li>
              <li><a href="#templates" className="text-sm hover:text-white transition-colors">Templates</a></li>
              <li><a href="#precos" className="text-sm hover:text-white transition-colors">Preços</a></li>
              <li><a href="#ia-generativa" className="text-sm hover:text-white transition-colors">IA generativa</a></li>
              <li><a href="#como-funciona" className="text-sm hover:text-white transition-colors">Como funciona</a></li>
            </ul>
          </div>

          {/* Empresa */}
          <div>
            <h4 className="text-white font-semibold mb-4">Empresa</h4>
            <ul className="space-y-2.5">
              <li><Link to="/sobre" className="text-sm hover:text-white transition-colors">Sobre nós</Link></li>
              <li><Link to="/blog" className="text-sm hover:text-white transition-colors">Blog</Link></li>
            </ul>
          </div>

          {/* Suporte */}
          <div>
            <h4 className="text-white font-semibold mb-4">Suporte</h4>
            <ul className="space-y-2.5">
              <li><a href="#faq" className="text-sm hover:text-white transition-colors">Perguntas frequentes</a></li>
              <li><Link to="/contato" className="text-sm hover:text-white transition-colors">Contato</Link></li>
              <li><Link to="/termos" className="text-sm hover:text-white transition-colors">Termos de uso</Link></li>
              <li><Link to="/privacidade" className="text-sm hover:text-white transition-colors">Privacidade</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm">© {new Date().getFullYear()} Mimo do Casal. Feito com 💕 para casais apaixonados.</p>
          <div className="flex items-center gap-2">
            <span className="text-xs">Seguro e confiável</span>
            <Shield size={14} className="text-green-400" />
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main export ─── */
// Banner topo: aparece SÓ se o visitante veio com ?ref=... ativo. Fica fixed
// no topo (z-60) e empurra o Navbar pra baixo (a Landing passa a altura como
// offset pro Navbar e padding-top pro conteúdo).
const REF_BANNER_HEIGHT = 44; // px — sincronizado com py-2.5 + texto
function ReferralBanner({ onDismiss }) {
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-rose-500 text-white shadow-md"
      style={{ height: REF_BANNER_HEIGHT }}
    >
      <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex items-center gap-3">
        <Gift size={16} className="flex-shrink-0" />
        <p className="flex-1 text-sm leading-snug">
          <span className="font-semibold">Vocês foram indicados!</span>
          <span className="hidden sm:inline"> Ao criar sua lista de casamento, vocês ganham <strong>R$ 50</strong> de crédito quando receberem o 1º presente.</span>
          <span className="sm:hidden"> Ganhe <strong>R$ 50</strong> ao criar sua lista.</span>
        </p>
        <Link
          to="/signup"
          className="hidden sm:inline-flex items-center gap-1.5 bg-white text-rose-600 px-4 py-1.5 rounded-full text-xs font-bold hover:scale-105 transition-transform whitespace-nowrap"
        >
          Criar minha lista <ArrowRight size={12} />
        </Link>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Fechar"
          className="flex-shrink-0 p-1 rounded-full hover:bg-white/15 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

export default function Landing() {
  const [refCode, setRefCode] = useState('');
  const [refDismissed, setRefDismissed] = useState(false);

  useEffect(() => {
    setRefCode(getActiveReferral() || '');
    try {
      if (sessionStorage.getItem('mdc:refBannerDismissed') === '1') setRefDismissed(true);
    } catch { /* ignore */ }
  }, []);

  const bannerVisible = !!refCode && !refDismissed;
  const dismissBanner = () => {
    setRefDismissed(true);
    try { sessionStorage.setItem('mdc:refBannerDismissed', '1'); } catch { /* ignore */ }
  };

  return (
    <div className="bg-white" style={{ paddingTop: bannerVisible ? REF_BANNER_HEIGHT : 0 }}>
      {bannerVisible && <ReferralBanner onDismiss={dismissBanner} />}
      <Navbar topOffset={bannerVisible ? REF_BANNER_HEIGHT : 0} />
      <Hero />
      <StatsBar />
      <HowItWorks />
      <PriceHighlight />
      <GiftShowcase />
      <RealWeddingsSection />
      <AISection />
      <TemplatesSection />
      <PricingSection />
      <Mimos3DSection />
      <ContactSection />
      <FAQSection />
      <FooterCTA />
      <Footer />
    </div>
  );
}
